"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { hydrateGuestCart } from "./actions";
import type { CartItem } from "./CartContext";
import type { CartAddon } from "./addons";
import { addonsKey } from "./addons";
import { cartItemKey } from "./cartItemKey";
const GUEST_CART_KEY = "uqhoan_cart";
type GuestCartItem = {
  product_id: string;
  variant_id?: string | null;
  quantity: number;
  addons?: CartAddon[] | null;
  product_unit_id?: string | null;
};

/**
 * Compact signature of the cart's row set — changes when an item is added or
 * removed but NOT when only quantities change. Used to trigger a re-hydrate so
 * newly-added items pick up product details (tiers, name, image, …).
 */
function structuralKey(items: {
  product_id: string;
  variant_id?: string | null;
  addons?: CartAddon[] | null;
  product_unit_id?: string | null;
}[]): string {
  return items.map(i => `${i.product_id}::${i.variant_id ?? ""}::${addonsKey(i.addons)}::${i.product_unit_id ?? ""}`).sort().join("|");
}
function readGuestCart(): GuestCartItem[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(GUEST_CART_KEY) ?? "[]");
  } catch {
    return [];
  }
}
function writeGuestCart(items: GuestCartItem[]): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
}
export type GuestCartHook = {
  items: CartItem[];
  /** True once localStorage has been read; false during SSR and first paint. */
  hydrated: boolean;
  addItem: (productId: string, qty: number, minQty: number, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null) => void;
  updateQty: (productId: string, qty: number, minQty?: number, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null) => void;
  removeItem: (productId: string, variantId?: string | null, addons?: CartAddon[] | null, productUnitId?: string | null) => void;
  clearCart: () => void;
  mergeAndClear: () => GuestCartItem[];
};
export function useGuestCart() {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [items, setItems] = useState(t0);
  const [hydrated, setHydrated] = useState(false);
  const lastStructuralKeyRef = useRef("");
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const stored = readGuestCart();
      setHydrated(true);
      const key = structuralKey(stored);
      if (key === lastStructuralKeyRef.current) {
        return;
      }
      lastStructuralKeyRef.current = key;
      if (stored.length === 0) {
        setItems([]);
        return;
      }
      setItems(prev => {
        if (prev.length > 0) {
          return prev;
        }
        return stored.map(_temp);
      });
      hydrateGuestCart(stored).then(hydrated_0 => {
        if (lastStructuralKeyRef.current === key) {
          setItems(hydrated_0);
        }
      });
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== items) {
    t2 = [items];
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function persist(updated) {
      writeGuestCart(updated.map(_temp2));
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const persist = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function addItem(productId, qty, minQty, variantId, addons, productUnitId) {
      setItems(prev_0 => {
        const key_0 = cartItemKey(productId, variantId, addonsKey(addons), productUnitId);
        const existing = prev_0.find(i_0 => cartItemKey(i_0.product_id, i_0.variant_id, addonsKey(i_0.addons), i_0.product_unit_id) === key_0);
        let updated_0;
        if (existing) {
          updated_0 = prev_0.map(i_1 => cartItemKey(i_1.product_id, i_1.variant_id, addonsKey(i_1.addons), i_1.product_unit_id) === key_0 ? {
            ...i_1,
            quantity: Math.max(i_1.quantity + qty, minQty)
          } : i_1);
        } else {
          updated_0 = [...prev_0, {
            product_id: productId,
            variant_id: variantId,
            quantity: Math.max(qty, minQty),
            addons: addons && addons.length > 0 ? addons : null,
            product_unit_id: productUnitId ?? null
          }];
        }
        persist(updated_0);
        return updated_0;
      });
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const addItem = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function updateQty(productId_0, qty_0, t6, variantId_0, addons_0, productUnitId_0) {
      const minQty_0 = t6 === undefined ? 1 : t6;
      const floored = Math.max(qty_0, minQty_0);
      setItems(prev_1 => {
        const key_1 = cartItemKey(productId_0, variantId_0, addonsKey(addons_0), productUnitId_0);
        const updated_1 = prev_1.map(i_2 => cartItemKey(i_2.product_id, i_2.variant_id, addonsKey(i_2.addons), i_2.product_unit_id) === key_1 ? {
          ...i_2,
          quantity: floored
        } : i_2);
        persist(updated_1);
        return updated_1;
      });
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const updateQty = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function removeItem(productId_1, variantId_1, addons_1, productUnitId_1) {
      setItems(prev_2 => {
        const key_2 = cartItemKey(productId_1, variantId_1, addonsKey(addons_1), productUnitId_1);
        const updated_2 = prev_2.filter(i_3 => cartItemKey(i_3.product_id, i_3.variant_id, addonsKey(i_3.addons), i_3.product_unit_id) !== key_2);
        persist(updated_2);
        return updated_2;
      });
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const removeItem = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function clearCart() {
      if (typeof window !== "undefined") {
        localStorage.removeItem(GUEST_CART_KEY);
      }
      setItems([]);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const clearCart = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = function mergeAndClear() {
      const current = readGuestCart();
      clearCart();
      return current;
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const mergeAndClear = t8;
  let t9;
  if ($[10] !== hydrated || $[11] !== items) {
    t9 = {
      items,
      hydrated,
      addItem,
      updateQty,
      removeItem,
      clearCart,
      mergeAndClear
    };
    $[10] = hydrated;
    $[11] = items;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  return t9;
}
function _temp2(i) {
  return {
    product_id: i.product_id,
    variant_id: i.variant_id,
    quantity: i.quantity,
    addons: i.addons ?? null,
    product_unit_id: i.product_unit_id ?? null
  };
}
function _temp(item) {
  return {
    product_id: item.product_id,
    variant_id: item.variant_id,
    quantity: item.quantity,
    addons: item.addons ?? null,
    product_unit_id: item.product_unit_id ?? null
  };
}